import { z } from "zod";
import { idSchema } from "./issues";

/** What a person is told of (BUILD_SPEC §15.4). */
export const NOTIFICATION_KINDS = [
  "assigned",
  "unassigned",
  "mentioned",
  "commented",
  "status",
  "resolved",
  "due",
  "overdue",
  "project_added",
  "project_admin",
  "automation",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

/** What a person is always told in their Inbox, whatever they keep quiet: mentions and assignments. */
export const QUIET_ALWAYS_TOLD: readonly NotificationKind[] = ["mentioned", "assigned", "unassigned"];

/** What a person is emailed of until they choose otherwise (BUILD_SPEC §15.4): their own work, and what's due. */
export const EMAILED_BY_DEFAULT: readonly NotificationKind[] = ["assigned", "mentioned", "due", "overdue"];

/** How often email comes: as it happens, or a digest each hour or each day (digests are a plan's feature). */
export const EMAIL_EVERY = ["instant", "hourly", "daily"] as const;
export type EmailEvery = (typeof EMAIL_EVERY)[number];

const HOUR = /^([01]\d|2[0-3]):00$/;

/** The Inbox's views (§12.8): everything, the unread, mentions, assignments. */
export const INBOX_FILTERS = ["all", "unread", "mentions", "assignments"] as const;
export type InboxFilter = (typeof INBOX_FILTERS)[number];

export const inboxQuerySchema = z
  .object({
    filter: z.enum(INBOX_FILTERS).default("all"),
    cursor: z
      .string()
      .regex(/^[A-Za-z0-9_-]{1,400}$/)
      .optional(),
  })
  .strict();

export const notificationIdsSchema = z.object({ ids: z.array(idSchema).min(1).max(500) }).strict();
export const markReadSchema = notificationIdsSchema.extend({ read: z.boolean() }).strict();

/** A person's own preferences (§15.4, §4.3): kept with them, so the same in every browser they use. */
export const preferencesSchema = z
  .object({
    mutedKinds: z.array(z.enum(NOTIFICATION_KINDS)).max(NOTIFICATION_KINDS.length).optional(),
    mutedProjects: z.array(idSchema).max(1_000).optional(),
    watchOnComment: z.boolean().optional(),
    watchOnMention: z.boolean().optional(),
    /** The hour due and overdue reminders come, where the person is: "09:00". */
    reminderTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):00$/, "Choose an hour, such as 09:00")
      .optional(),
    timezone: z.string().max(64).nullable().optional(),
    /** Whether single-key shortcuts are on (§4.3). */
    shortcuts: z.boolean().optional(),
    /** What is emailed (§15.4): none at all once they stop every email. */
    emailKinds: z.array(z.enum(NOTIFICATION_KINDS)).max(NOTIFICATION_KINDS.length).optional(),
    emailEvery: z.enum(EMAIL_EVERY).optional(),
    /** Hours no email comes, where the person is: what falls in them waits until they end. */
    quietHours: z
      .object({
        from: z.string().regex(HOUR, "Choose an hour, such as 22:00"),
        to: z.string().regex(HOUR, "Choose an hour, such as 07:00"),
      })
      .strict()
      .nullable()
      .optional(),
  })
  .strict();
export type PreferencesInput = z.infer<typeof preferencesSchema>;
